import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { usuariosAPI, movimientosPredefinidosAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import '../Usuarios.css';

// Misma rama que el menú lateral. Un permiso repetido en varias ramas es el mismo: marcarlo en una lo marca en las otras.
const ESTRUCTURA_PERMISOS = [
  {
    titulo: 'Dashboard',
    submenus: [
      { titulo: 'Dashboard', items: ['ver_informes'] },
    ],
  },
  {
    titulo: 'Clientes',
    submenus: [
      { titulo: 'Lista', items: ['ver_clientes', 'crear_clientes', 'editar_clientes', 'eliminar_clientes'] },
      { titulo: 'Fichas', items: ['ver_informes'] },
      { titulo: 'Tipos de Clientes', items: ['gestionar_usuarios'] },
      { titulo: 'Grupos', items: ['ver_clientes', 'gestionar_grupos', 'enviar_grupos'] },
      { titulo: 'Movimientos clientes', items: ['ver_movimientos', 'ver_movimientos_clientes', 'crear_debitos', 'crear_debitos_clientes', 'crear_creditos', 'crear_creditos_clientes', 'anular_recibos'] },
      { titulo: 'Facturación', items: ['ver_facturacion', 'emitir_facturas'] },
    ],
  },
  {
    titulo: 'Proveedores',
    submenus: [
      { titulo: 'Lista', items: ['ver_clientes', 'crear_clientes', 'editar_clientes', 'eliminar_clientes'] },
      { titulo: 'Fichas', items: ['ver_informes'] },
      { titulo: 'Tipos de Proveedores', items: ['gestionar_usuarios'] },
      { titulo: 'Movimientos proveedores', items: ['ver_movimientos', 'ver_movimientos_proveedores', 'crear_debitos', 'crear_debitos_proveedores', 'crear_creditos', 'crear_creditos_proveedores', 'anular_pagos', 'gestionar_op_huerfanas'] },
      { titulo: 'Gastos', items: ['ver_gastos', 'crear_gastos', 'anular_gastos'] },
    ],
  },
  {
    titulo: 'Fondos',
    submenus: [
      { titulo: 'Lista de fondos', items: ['ver_fondos_listado'] },
      { titulo: 'Cierre de caja', items: ['cerrar_caja'] },
      { titulo: 'Historial cierres', items: ['cerrar_caja', 'ver_fondos_listado'] },
      { titulo: 'Proyección fondos', items: ['ver_informes'] },
      { titulo: 'Conciliación bancaria', items: ['conciliar_cuentas_bancarias'] },
      { titulo: 'Fichas', items: ['ver_informes'] },
      { titulo: 'Movimientos fondos', items: ['ver_movimientos', 'ver_fondos_listado', 'crear_movimientos_fondos'] },
      { titulo: 'Cartera de cheques', items: ['ver_movimientos'] },
      { titulo: 'Cheques emitidos', items: ['ver_movimientos'] },
      { titulo: 'Formas de Pago', items: ['ver_movimientos', 'crear_debitos'] },
      { titulo: 'Fondos por usuario', items: ['ver_movimientos', 'gestionar_usuarios'] },
      { titulo: 'Cajas', items: ['ver_movimientos', 'crear_debitos'] },
      { titulo: 'Bancos', items: ['ver_bancos', 'gestionar_bancos'] },
      { titulo: 'Cuentas Bancarias', items: ['ver_cuentas_bancarias', 'gestionar_cuentas_bancarias'] },
    ],
  },
  {
    titulo: 'Empleados',
    submenus: [
      { titulo: 'Lista', items: ['ver_clientes', 'crear_clientes', 'editar_clientes'] },
      { titulo: 'Fichas', items: ['ver_informes'] },
      { titulo: 'Movimientos de personas', items: ['ver_clientes', 'gestionar_usuarios'] },
      { titulo: 'Movimientos y pagos', items: ['ver_movimientos_empleados', 'crear_debitos_empleados', 'crear_pagos_empleados', 'devengar_sueldos', 'anular_pagos_empleados'] },
    ],
  },
  {
    titulo: 'Movimientos predefinidos',
    submenus: [
      { titulo: 'Ver y editar', items: ['ver_movimientos', 'crear_debitos'] },
    ],
  },
  {
    titulo: 'Movimientos programados',
    submenus: [
      { titulo: 'Programación masiva', items: ['ver_movimientos_programados', 'crear_movimientos_programados', 'aplicar_movimientos_programados'] },
    ],
  },
  {
    titulo: 'Informes',
    submenus: [
      { titulo: 'Informes', items: ['ver_informes'] },
      { titulo: 'Proyección fondos', items: ['ver_informes'] },
      { titulo: 'Resultados reales', items: ['ver_resultados_reales'] },
      { titulo: 'Fichas del dashboard', items: ['ver_informes'] },
      { titulo: 'Mensajes', items: ['ver_clientes'] },
      { titulo: 'Carga masiva', items: ['carga_masiva'] },
    ],
  },
  {
    titulo: 'Configuración',
    submenus: [
      { titulo: 'General', items: ['gestionar_usuarios'] },
      { titulo: 'Puesta en marcha', items: ['admin_puesta_en_marcha', 'gestionar_usuarios'] },
    ],
  },
  {
    titulo: 'Seguridad',
    submenus: [
      { titulo: 'Usuarios', items: ['gestionar_usuarios'] },
      { titulo: 'Auditoría', items: ['ver_auditoria'] },
      { titulo: 'Backups', items: ['ver_backup', 'ejecutar_backup', 'restaurar_backup', 'eliminar_backup', 'configurar_seguridad_backup'] },
    ],
  },
];

function CheckboxRama({ claves, seleccionados, disabled, onMarcar }) {
  const unicos = [...new Set(claves)];
  const marcados = unicos.filter((clave) => seleccionados.some((item) => String(item) === String(clave))).length;
  const completo = unicos.length > 0 && marcados === unicos.length;
  return (
    <input
      ref={(el) => {
        if (el) el.indeterminate = marcados > 0 && !completo;
      }}
      type="checkbox"
      checked={completo}
      disabled={disabled || unicos.length === 0}
      onClick={(evento) => evento.stopPropagation()}
      onChange={() => onMarcar(unicos, !completo)}
    />
  );
}

function nombresDeNodo(nodo) {
  return nodo.submenus ? nodo.submenus.flatMap((sub) => sub.items) : (nodo.items || []);
}

function ArbolPermisos({ estructura, permisosByNombre, seleccionados, disabled, onToggleUno, onToggleRama, otros }) {
  const visibles = (nombres) => nombres.filter((nombre) => permisosByNombre[nombre]);
  return (
    <div className="permisos-menu-estructura permisos-tree">
      {estructura.map((seccion) => {
        const deSeccion = visibles(nombresDeNodo(seccion));
        const submenus = (seccion.submenus || [])
          .map((sub) => ({ ...sub, items: visibles(sub.items) }))
          .filter((sub) => sub.items.length > 0);
        if (!deSeccion.length && !submenus.length) return null;
        return (
          <div key={seccion.titulo} className="permisos-seccion permisos-rama-fila">
            <CheckboxRama
              claves={deSeccion}
              seleccionados={seleccionados}
              disabled={disabled}
              onMarcar={onToggleRama}
            />
            <details>
              <summary>
                <span className="permisos-chevron" aria-hidden="true" />
                <span>{seccion.titulo}</span>
              </summary>
            {submenus.map((sub) => (
              <div key={sub.titulo} className="permisos-subseccion permisos-rama-fila">
                <CheckboxRama
                  claves={sub.items}
                  seleccionados={seleccionados}
                  disabled={disabled}
                  onMarcar={onToggleRama}
                />
                <details>
                  <summary>
                    <span className="permisos-chevron" aria-hidden="true" />
                    <span>{sub.titulo}</span>
                  </summary>
                <div className="permisos-grid">
                  {sub.items.map((nombre) => {
                    const permiso = permisosByNombre[nombre];
                    return (
                      <label key={permiso.id} className="permiso-checkbox">
                        <input
                          type="checkbox"
                          checked={seleccionados.includes(permiso.nombre)}
                          disabled={disabled}
                          onChange={() => onToggleUno(permiso.nombre)}
                        />
                        <span className="permiso-label">{permiso.descripcion || permiso.nombre}</span>
                      </label>
                    );
                  })}
                </div>
                </details>
              </div>
            ))}
            </details>
          </div>
        );
      })}
      {otros.length > 0 && (
        <div className="permisos-seccion permisos-rama-fila">
          <CheckboxRama
            claves={otros.map((permiso) => permiso.nombre)}
            seleccionados={seleccionados}
            disabled={disabled}
            onMarcar={onToggleRama}
          />
          <details>
            <summary>
              <span className="permisos-chevron" aria-hidden="true" />
              <span>Otros</span>
            </summary>
          <div className="permisos-grid">
            {otros.map((permiso) => (
              <label key={permiso.id} className="permiso-checkbox">
                <input
                  type="checkbox"
                  checked={seleccionados.includes(permiso.nombre)}
                  disabled={disabled}
                  onChange={() => onToggleUno(permiso.nombre)}
                />
                <span className="permiso-label">{permiso.descripcion || permiso.nombre}</span>
              </label>
            ))}
          </div>
          </details>
        </div>
      )}
    </div>
  );
}

function ArbolPredefinidos({ grupos, seleccionados, disabled, onToggleUno, onToggleGrupo }) {
  return (
    <div className="permisos-menu-estructura permisos-tree">
      {grupos.map((grupo) => {
        const ids = grupo.items.map((item) => item.id);
        return (
          <div key={grupo.uso} className="permisos-seccion permisos-rama-fila">
            <CheckboxRama
              claves={ids}
              seleccionados={seleccionados}
              disabled={disabled}
              onMarcar={onToggleGrupo}
            />
            <details>
              <summary>
                <span className="permisos-chevron" aria-hidden="true" />
                <span>{grupo.titulo}</span>
              </summary>
            <div className="permisos-grid">
              {grupo.items.map((item) => (
                <label key={item.id} className="permiso-checkbox">
                  <input
                    type="checkbox"
                    checked={seleccionados.some((id) => Number(id) === Number(item.id))}
                    disabled={disabled}
                    onChange={() => onToggleUno(item.id)}
                  />
                  <span className="permiso-label">{item.descripcion} ({item.tipo_movimiento})</span>
                </label>
              ))}
            </div>
            </details>
          </div>
        );
      })}
    </div>
  );
}

const estadoInicialForm = {
  username: '',
  password: '',
  nombre: '',
  email: '',
  activo: true,
  permisos: [],
  movimientos_predefinidos_ids: []
};

const UsuarioForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser, refreshUser, hasPermission } = useAuth();
  const esRutaNuevo = location.pathname.endsWith('/nuevo');

  const [loading, setLoading] = useState(false);
  const [permisosDisponibles, setPermisosDisponibles] = useState([]);
  const [catalogoPredefinidos, setCatalogoPredefinidos] = useState([]);
  const [formData, setFormData] = useState(estadoInicialForm);

  const loadPermisos = useCallback(async () => {
    try {
      const response = await usuariosAPI.getPermisos();
      if (response.success) {
        setPermisosDisponibles(response.data);
      }
    } catch (error) {
      console.error('Error al cargar permisos:', error);
    }
  }, []);

  const loadCatalogoPredefinidos = useCallback(async () => {
    if (!hasPermission('gestionar_usuarios')) return;
    try {
      const response = await movimientosPredefinidosAPI.getAll({ activo: true, todos: 1 });
      if (response.success) {
        setCatalogoPredefinidos(response.data || []);
      }
    } catch (error) {
      console.error('Error al cargar movimientos predefinidos:', error);
    }
  }, [hasPermission]);

  const loadUsuario = useCallback(async () => {
    try {
      const response = await usuariosAPI.getById(id);
      if (response.success) {
        const usuarioData = {
          ...response.data,
          password: '' // No mostrar la contraseña
        };
        
        // Si es admin, asegurar que tenga todos los permisos visibles
        if (usuarioData.username === 'admin' && permisosDisponibles.length > 0) {
          usuarioData.permisos = permisosDisponibles.map(p => p.nombre);
        }
        if (!Array.isArray(usuarioData.movimientos_predefinidos_ids)) {
          usuarioData.movimientos_predefinidos_ids = [];
        }
        if (usuarioData.username === 'admin' && catalogoPredefinidos.length > 0) {
          usuarioData.movimientos_predefinidos_ids = catalogoPredefinidos.map((p) => p.id);
        }
        
        setFormData(usuarioData);
      }
    } catch (error) {
      toast.error('Error al cargar usuario');
      navigate('/usuarios');
    }
  }, [id, navigate, permisosDisponibles, catalogoPredefinidos]);

  useEffect(() => {
    loadPermisos();
    loadCatalogoPredefinidos();
  }, [loadPermisos, loadCatalogoPredefinidos]);

  useEffect(() => {
    if (esRutaNuevo) {
      setFormData({ ...estadoInicialForm });
      return;
    }
    if (id && permisosDisponibles.length > 0) {
      loadUsuario();
    }
  }, [esRutaNuevo, id, permisosDisponibles.length, loadUsuario]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePermisoChange = (permisoNombre) => {
    setFormData(prev => {
      const permisos = prev.permisos.includes(permisoNombre)
        ? prev.permisos.filter(p => p !== permisoNombre)
        : [...prev.permisos, permisoNombre];
      return { ...prev, permisos };
    });
  };

  const handleSeleccionarTodos = (nombresPermisos) => {
    setFormData(prev => {
      const set = new Set(prev.permisos);
      nombresPermisos.forEach(p => set.add(p));
      return { ...prev, permisos: [...set] };
    });
  };

  const handleQuitarTodos = (nombresPermisos) => {
    setFormData(prev => ({
      ...prev,
      permisos: prev.permisos.filter(p => !nombresPermisos.includes(p))
    }));
  };

  const handlePredefinidoChange = (predefId) => {
    const id = Number(predefId);
    setFormData((prev) => {
      const ids = prev.movimientos_predefinidos_ids || [];
      const tiene = ids.some((x) => Number(x) === id);
      return {
        ...prev,
        movimientos_predefinidos_ids: tiene
          ? ids.filter((x) => Number(x) !== id)
          : [...ids, id]
      };
    });
  };

  const handleSeleccionarPredefinidosGrupo = (idsGrupo) => {
    setFormData((prev) => {
      const set = new Set((prev.movimientos_predefinidos_ids || []).map(Number));
      idsGrupo.forEach((id) => set.add(Number(id)));
      return { ...prev, movimientos_predefinidos_ids: [...set] };
    });
  };

  const handleQuitarPredefinidosGrupo = (idsGrupo) => {
    const quitar = new Set(idsGrupo.map(Number));
    setFormData((prev) => ({
      ...prev,
      movimientos_predefinidos_ids: (prev.movimientos_predefinidos_ids || []).filter(
        (id) => !quitar.has(Number(id))
      )
    }));
  };

  const esAdmin = formData.username === 'admin';

  const labelUsoPredef = (uso) => {
    if (uso === 'proveedores') return 'Proveedores';
    if (uso === 'fondos') return 'Fondos';
    return 'Clientes';
  };

  const gruposPredefinidos = ['clientes', 'proveedores', 'fondos'].map((uso) => ({
    uso,
    titulo: labelUsoPredef(uso),
    items: catalogoPredefinidos.filter((p) => (p.uso || 'clientes') === uso)
  })).filter((g) => g.items.length > 0);

  const permisosByNombre = permisosDisponibles.reduce((acc, p) => {
    acc[p.nombre] = p;
    return acc;
  }, {});

  const todosLosPermisosEnEstructura = new Set(
    ESTRUCTURA_PERMISOS.flatMap((seccion) =>
      seccion.submenus
        ? seccion.submenus.flatMap((sub) => sub.items)
        : seccion.items
    )
  );
  const otrosPermisos = permisosDisponibles.filter((p) => !todosLosPermisosEnEstructura.has(p.nombre));
  const permisosSeleccionados = esAdmin && id
    ? permisosDisponibles.map((permiso) => permiso.nombre)
    : (formData.permisos || []);
  const predefinidosSeleccionados = esAdmin && id
    ? catalogoPredefinidos.map((item) => item.id)
    : (formData.movimientos_predefinidos_ids || []);

  const alternarRama = (nombres, marcar) => {
    if (marcar) handleSeleccionarTodos(nombres);
    else handleQuitarTodos(nombres);
  };

  const alternarGrupoPredefinido = (ids, marcar) => {
    if (marcar) handleSeleccionarPredefinidosGrupo(ids);
    else handleQuitarPredefinidosGrupo(ids);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const dataToSend = { ...formData };
      if (!dataToSend.password) {
        delete dataToSend.password; // No enviar password vacío en edición
      }

      // Protección para usuario admin
      if (esAdmin && id) {
        // Forzar activo = true para admin
        dataToSend.activo = true;
        // No enviar permisos - el backend los asignará automáticamente
        delete dataToSend.permisos;
        delete dataToSend.movimientos_predefinidos_ids;
      } else {
        // Para usuarios normales, asegurar que permisos sea un array
        if (!Array.isArray(dataToSend.permisos)) {
          dataToSend.permisos = [];
        }
        if (!Array.isArray(dataToSend.movimientos_predefinidos_ids)) {
          dataToSend.movimientos_predefinidos_ids = [];
        }
      }

      let response;
      if (id) {
        response = await usuariosAPI.update(id, dataToSend);
      } else {
        if (!dataToSend.password) {
          toast.error('La contraseña es requerida para nuevos usuarios');
          setLoading(false);
          return;
        }
        response = await usuariosAPI.create(dataToSend);
      }

      if (response.success) {
        toast.success(id ? 'Usuario actualizado exitosamente' : 'Usuario creado exitosamente');
        if (id && currentUser && String(id) === String(currentUser.id)) {
          await refreshUser();
        }
        navigate('/usuarios');
      }
    } catch (error) {
      toast.error(error.message || 'Error al guardar usuario');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="usuario-form">
      <h1>{id ? 'Editar Usuario' : 'Nuevo Usuario'}</h1>
      
      <form onSubmit={handleSubmit} className="form-container">
        <div className="form-row">
          <div className="form-group">
            <label>Usuario *</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              disabled={esAdmin && id}
              title={esAdmin && id ? "No se puede cambiar el username del administrador" : ""}
            />
            {esAdmin && id && (
              <small style={{ color: '#666', fontStyle: 'italic' }}>
                El username del administrador no se puede modificar
              </small>
            )}
          </div>
          <div className="form-group">
            <label>Contraseña {!id && '*'}</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required={!id}
              placeholder={id ? "Dejar vacío para no cambiar" : ""}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Nombre *</label>
            <input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-toggle-wrap">
            <span className="toggle-switch-box">
              <input
                type="checkbox"
                name="activo"
                checked={formData.activo}
                onChange={handleChange}
                disabled={esAdmin && id}
              />
              <span className="form-toggle-switch" />
            </span>
            <span className="form-toggle-label">
              {formData.activo ? 'Desactivar' : 'Activar'}
              {esAdmin && id && (
                <span style={{ color: '#666', fontStyle: 'italic', marginLeft: '8px' }}>
                  (El administrador siempre está activo)
                </span>
              )}
            </span>
          </label>
        </div>

        <div className="form-group permisos-group">
          <label>Permisos</label>
          {esAdmin && id ? (
            <div style={{ 
              padding: '15px', 
              background: '#e7f3ff', 
              border: '1px solid #2196F3', 
              borderRadius: '4px',
              marginTop: '10px'
            }}>
              <strong style={{ color: '#1976D2' }}>Usuario Administrador</strong>
              <p style={{ margin: '8px 0 0 0', color: '#555' }}>
                El usuario administrador tiene acceso completo a todos los módulos del sistema.
                Los permisos no se pueden modificar.
              </p>
              <div style={{ marginTop: '12px' }}>
                <strong>Permisos asignados (misma estructura que el menú):</strong>
                <ArbolPermisos
                  estructura={ESTRUCTURA_PERMISOS}
                  permisosByNombre={permisosByNombre}
                  seleccionados={permisosSeleccionados}
                  disabled
                  onToggleUno={handlePermisoChange}
                  onToggleRama={alternarRama}
                  otros={otrosPermisos}
                />
              </div>
            </div>
          ) : (
            <ArbolPermisos
              estructura={ESTRUCTURA_PERMISOS}
              permisosByNombre={permisosByNombre}
              seleccionados={permisosSeleccionados}
              onToggleUno={handlePermisoChange}
              onToggleRama={alternarRama}
              otros={otrosPermisos}
            />
          )}
        </div>

        {hasPermission('gestionar_usuarios') && catalogoPredefinidos.length > 0 && (
          <div className="form-group permisos-group">
            <label>Movimientos predefinidos permitidos</label>
            <p style={{ fontSize: '0.9rem', color: '#555', margin: '0 0 12px 0' }}>
              Define qué plantillas de movimiento puede usar este usuario al operar (Recibo de cobro, Orden de pago, etc.).
            </p>
            {esAdmin && id ? (
              <div style={{
                padding: '15px',
                background: '#e7f3ff',
                border: '1px solid #2196F3',
                borderRadius: '4px'
              }}>
                <strong style={{ color: '#1976D2' }}>Administrador</strong>
                <p style={{ margin: '8px 0 12px 0', color: '#555' }}>
                  Tiene acceso a todos los movimientos predefinidos activos.
                </p>
                <ArbolPredefinidos
                  grupos={gruposPredefinidos}
                  seleccionados={predefinidosSeleccionados}
                  disabled
                  onToggleUno={handlePredefinidoChange}
                  onToggleGrupo={alternarGrupoPredefinido}
                />
              </div>
            ) : (
              <ArbolPredefinidos
                grupos={gruposPredefinidos}
                seleccionados={predefinidosSeleccionados}
                onToggleUno={handlePredefinidoChange}
                onToggleGrupo={alternarGrupoPredefinido}
              />
            )}
          </div>
        )}

        <div className="form-actions">
          <button type="button" onClick={() => navigate('/usuarios')} className="btn-secondary">
            Cancelar
          </button>
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UsuarioForm;

